import { useState } from "react";
import { BRAIN_API, getCsrfToken } from "../../lib/founderApi";

const INPUT = "w-full border border-[#E5E0D8] bg-[#FAF7F2] px-3 py-2 text-sm focus:outline-none focus:border-[#0B3D5C]";

const BLANK = {
  title: "", question: "", decision: "", reasoning: "",
  expected_outcome: "", reversibility: "reversible", tags: "",
};

function slugify(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

function errorMessage(detail) {
  if (typeof detail === "string") return detail;
  return detail?.[0]?.msg || "Write failed";
}

export function LogDecisionForm({ onLogged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [f, setF] = useState(BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    const body = {
      brain_id: `decision.founder-log.${slugify(f.title)}-${Date.now().toString(36)}`,
      collection: "decisions",
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      payload: {
        title: f.title, question: f.question, decision: f.decision,
        alternatives_considered: [], reasoning: f.reasoning,
        expected_outcome: f.expected_outcome,
        reversibility: f.reversibility, related_decisions: [],
      },
    };
    try {
      const res = await fetch(`${BRAIN_API}/founder/log-decision`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(errorMessage((await res.json()).detail));
      setMsg(`Logged: ${body.brain_id}`);
      setF(BLANK);
      onLogged();
    } catch (err) {
      setMsg(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E0D8] p-6">
      <button
        data-testid="log-decision-toggle"
        onClick={() => setOpen(!open)}
        className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#E07B3A]"
      >
        {open ? "− Close" : "+ Log a decision"}
      </button>
      {open && (
        <form onSubmit={submit} className="mt-4 grid gap-3" data-testid="log-decision-form">
          <input data-testid="ld-title" className={INPUT} placeholder="Title" value={f.title} onChange={set("title")} required />
          <input data-testid="ld-question" className={INPUT} placeholder="Question being decided" value={f.question} onChange={set("question")} required />
          <textarea data-testid="ld-decision" className={INPUT} rows={2} placeholder="The decision" value={f.decision} onChange={set("decision")} required />
          <textarea data-testid="ld-reasoning" className={INPUT} rows={3} placeholder="Reasoning" value={f.reasoning} onChange={set("reasoning")} required />
          <input data-testid="ld-expected" className={INPUT} placeholder="Expected outcome" value={f.expected_outcome} onChange={set("expected_outcome")} required />
          <div className="flex gap-3">
            <select data-testid="ld-reversibility" className={INPUT} value={f.reversibility} onChange={set("reversibility")}>
              <option value="reversible">reversible</option>
              <option value="expensive-to-reverse">expensive-to-reverse</option>
              <option value="irreversible">irreversible</option>
            </select>
            <input data-testid="ld-tags" className={INPUT} placeholder="tags, comma, separated" value={f.tags} onChange={set("tags")} />
          </div>
          <button
            data-testid="ld-submit"
            disabled={busy}
            className="bg-[#E07B3A] text-white font-bold px-5 py-2.5 text-sm w-fit disabled:opacity-50"
          >
            {busy ? "Writing…" : "Write to Brain"}
          </button>
          {msg && <p data-testid="ld-message" className="text-xs text-[#0B3D5C] font-semibold">{msg}</p>}
        </form>
      )}
    </div>
  );
}
